import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { SOLUTIONS } from "../data/content";

const FeatureCarousel = () => {
  const [index, setIndex] = useState(0);
  const active = SOLUTIONS[index];

  const go = (dir) => setIndex((i) => (i + dir + SOLUTIONS.length) % SOLUTIONS.length);

  return (
    <section id="feature" className="relative overflow-hidden bg-navy py-24 text-white sm:py-32 lg:py-40" data-testid="feature-carousel">
      <div className="pointer-events-none absolute inset-0 grid-bg opacity-[0.07]" />
      <div className="pointer-events-none absolute -right-40 top-1/2 h-96 w-96 -translate-y-1/2 rounded-full bg-teal/25 blur-[130px]" />

      <div className="relative mx-auto max-w-[1400px] px-6 sm:px-8 lg:px-12">
        <Reveal className="mb-14 max-w-3xl">
          <Eyebrow className="text-teal">06 — Feature Highlight</Eyebrow>
          <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
            See the platforms in action.
          </h2>
          <p className="mt-5 text-base text-white/70 sm:text-lg">
            Interactive dashboards designed for clarity, speed and enterprise-grade decision making.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="relative aspect-[16/10] overflow-hidden rounded-2xl border border-white/10 bg-white/5 shadow-2xl">
              <AnimatePresence mode="wait">
                <motion.img
                  key={active.id}
                  src={active.image}
                  alt={active.name}
                  initial={{ opacity: 0, scale: 1.04 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute inset-0 h-full w-full object-cover object-top"
                />
              </AnimatePresence>
            </div>
          </div>

          <div className="lg:col-span-4">
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4 }}
              >
                <span className="text-sm font-semibold uppercase tracking-wider text-teal">
                  {active.subtitle}
                </span>
                <h3 className="mt-2 font-display text-3xl font-bold">
                  {active.name === "AYOBELL" ? "AYOBELL Care" : active.name}
                </h3>
                <p className="mt-3 text-white/70">{active.description}</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {active.features.slice(0, 6).map((f) => (
                    <span key={f} className="rounded-full bg-white/10 px-3 py-1 text-xs font-medium">
                      {f}
                    </span>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="mt-8 flex items-center gap-3">
              <button
                onClick={() => go(-1)}
                data-testid="carousel-prev"
                aria-label="Previous"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 transition-colors hover:bg-white/10"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => go(1)}
                data-testid="carousel-next"
                aria-label="Next"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 transition-colors hover:bg-white/10"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
              <div className="ml-2 flex gap-2">
                {SOLUTIONS.map((s, i) => (
                  <button
                    key={s.id}
                    onClick={() => setIndex(i)}
                    aria-label={`Show ${s.name}`}
                    className={`h-2 rounded-full transition-all ${i === index ? "w-8 bg-teal" : "w-2 bg-white/30"}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeatureCarousel;
